(() => {
  /* Naylumi — sistema único y compartido de favoritos (home, catálogo,
     producto, header). Persiste en localStorage bajo una sola clave global
     y sincroniza todos los corazones de la página vía un evento custom, sin
     recargar ni navegar. El drawer se renderiza una sola vez (ver
     snippets/naylumi-favorites-drawer.liquid, incluido desde el header, que
     está presente en toda página) y su contenido se pinta dinámicamente
     aquí a partir del estado real. */

  const STORAGE_KEY = 'naylumi_favorites';
  const CHANGE_EVENT = 'naylumi:favorites:change';

  const readFavorites = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  const writeFavorites = (list) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) {
      /* localStorage no disponible (modo privado, cuota, etc.): la sesión
         sigue funcionando en memoria para esta carga de página. */
    }
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { favorites: list } }));
  };

  const isFavorite = (handle) => {
    if (!handle) return false;
    return readFavorites().some((item) => item.handle === handle);
  };

  const toggleFavorite = (product) => {
    if (!product || !product.handle) return null;
    const list = readFavorites();
    const index = list.findIndex((item) => item.handle === product.handle);
    let active;
    if (index > -1) {
      list.splice(index, 1);
      active = false;
    } else {
      list.push(product);
      active = true;
    }
    writeFavorites(list);
    return active;
  };

  const removeFavorite = (handle) => {
    writeFavorites(readFavorites().filter((item) => item.handle !== handle));
  };

  window.NaylumiFavorites = { readFavorites, toggleFavorite, removeFavorite, isFavorite };

  const productFromButton = (btn) => ({
    handle: btn.dataset.favHandle || '',
    url: btn.dataset.favUrl || '',
    title: btn.dataset.favTitle || '',
    image: btn.dataset.favImage || '',
    price: btn.dataset.favPrice || '',
    compare_at_price: btn.dataset.favCompareAtPrice || '',
    available: btn.dataset.favAvailable === 'true',
  });

  const setButtonVisualState = (btn, active) => {
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', String(active));
    const glyph = btn.querySelector('.fav-glyph');
    if (glyph) glyph.textContent = active ? '♥' : '♡';
  };

  const syncAllButtons = (favorites) => {
    document.querySelectorAll('[data-naylumi-fav-toggle][data-fav-handle]').forEach((btn) => {
      const handle = btn.dataset.favHandle;
      if (!handle) return;
      const active = favorites.some((item) => item.handle === handle);
      setButtonVisualState(btn, active);
    });
  };

  const initToggleButtons = (root) => {
    root.querySelectorAll('[data-naylumi-fav-toggle]').forEach((btn) => {
      if (btn.dataset.naylumiFavBound) return;
      btn.dataset.naylumiFavBound = 'true';

      const handle = btn.dataset.favHandle;
      if (handle) {
        setButtonVisualState(btn, isFavorite(handle));
      }

      btn.addEventListener('click', (event) => {
        event.preventDefault();
        if (handle) {
          const active = toggleFavorite(productFromButton(btn));
          setButtonVisualState(btn, !!active);
        } else {
          /* Tarjeta demo sin producto real: feedback visual únicamente,
             nunca se guarda como favorito comprable. */
          setButtonVisualState(btn, !btn.classList.contains('is-active'));
        }
      });
    });
  };

  /* ---------- Contador del header ---------- */
  const updateCounters = (favorites) => {
    document.querySelectorAll('[data-naylumi-favorites-count]').forEach((el) => {
      el.textContent = String(favorites.length);
      el.hidden = favorites.length === 0;
    });
  };

  /* ---------- Drawer ---------- */
  let drawerLastFocused = null;

  const escapeHtml = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (char) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]
    ));

  const renderDrawerList = (favorites) => {
    const list = document.querySelector('[data-naylumi-favorites-list]');
    if (!list) return;

    if (favorites.length === 0) {
      list.innerHTML = `
        <div class="nfav__empty">
          <span class="nfav__empty-icon" aria-hidden="true">♡</span>
          <p class="nfav__empty-title">Todavía no tienes favoritos</p>
          <p class="nfav__empty-text">Toca el corazón en cualquier producto para guardarlo aquí.</p>
        </div>
      `;
      return;
    }

    list.innerHTML = favorites
      .map((item) => `
        <div class="nfav__item" data-handle="${escapeHtml(item.handle)}">
          <a href="${escapeHtml(item.url)}" class="nfav__item-media">
            ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">` : ''}
          </a>
          <div class="nfav__item-body">
            <a href="${escapeHtml(item.url)}" class="nfav__item-title">${escapeHtml(item.title)}</a>
            <div class="nfav__item-price-row">
              <span class="nfav__item-price">${escapeHtml(item.price)}</span>
              ${item.compare_at_price ? `<span class="nfav__item-compare">${escapeHtml(item.compare_at_price)}</span>` : ''}
            </div>
            ${item.available === false ? '<span class="nfav__item-soldout">Agotado</span>' : ''}
            <div class="nfav__item-actions">
              <a href="${escapeHtml(item.url)}" class="nfav__item-view">Ver producto</a>
              <button type="button" class="nfav__item-remove" data-naylumi-fav-remove data-handle="${escapeHtml(item.handle)}">Quitar</button>
            </div>
          </div>
        </div>
      `)
      .join('');
  };

  const openDrawer = () => {
    const drawer = document.querySelector('[data-naylumi-favorites-drawer]');
    if (!drawer) return;
    drawerLastFocused = document.activeElement;
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    const closeBtn = drawer.querySelector('[data-naylumi-favorites-close]');
    closeBtn?.focus();
    document.addEventListener('keydown', onDrawerKeydown);
  };

  const closeDrawer = () => {
    const drawer = document.querySelector('[data-naylumi-favorites-drawer]');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onDrawerKeydown);
    if (drawerLastFocused && typeof drawerLastFocused.focus === 'function') {
      drawerLastFocused.focus();
    }
  };

  const onDrawerKeydown = (event) => {
    if (event.key === 'Escape') closeDrawer();
  };

  const initDrawer = () => {
    const drawer = document.querySelector('[data-naylumi-favorites-drawer]');
    if (!drawer || drawer.dataset.naylumiFavDrawerBound) return;
    drawer.dataset.naylumiFavDrawerBound = 'true';

    /* El drawer se renderiza dentro de la sección del header, que Shopify
       envuelve en un .shopify-section con su propio stacking context
       (position:relative + z-index:1, ver naylumi-global-background.liquid).
       Secciones posteriores del home (hero, catálogo, etc.) reciben ese
       mismo z-index:1 y, al ir después en el DOM, pintan por encima de esa
       sección completa — dejando el drawer "atravesado" pese a su propio
       z-index interno. Se reubica como hijo directo de <body> para que su
       z-index compita en el nivel superior real de la página. */
    document.body.appendChild(drawer);

    drawer.querySelectorAll('[data-naylumi-favorites-close]').forEach((trigger) => {
      trigger.addEventListener('click', closeDrawer);
    });

    drawer.addEventListener('click', (event) => {
      const removeBtn = event.target.closest('[data-naylumi-fav-remove]');
      if (removeBtn) {
        removeFavorite(removeBtn.dataset.handle);
      }
    });

    document.querySelectorAll('[data-naylumi-favorites-open]').forEach((trigger) => {
      if (trigger.dataset.naylumiFavOpenBound) return;
      trigger.dataset.naylumiFavOpenBound = 'true';
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        openDrawer();
      });
    });

    renderDrawerList(readFavorites());
  };

  const init = (root = document) => {
    initToggleButtons(root);
    initDrawer();
  };

  document.addEventListener(CHANGE_EVENT, (event) => {
    const favorites = event.detail.favorites;
    syncAllButtons(favorites);
    updateCounters(favorites);
    renderDrawerList(favorites);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      updateCounters(readFavorites());
    }, { once: true });
  } else {
    init();
    updateCounters(readFavorites());
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    updateCounters(readFavorites());
  });
})();
